import { useState, useEffect } from 'react';
import { useQueryParams, useMessage } from './';
import { searchService } from '../services';

export const useSearch = ({
  objectName = undefined,
  setLoading = undefined,
  pagination = true,
  enableHistory = true,
  populateWithDefaultValues = false,
  useDefaultValues = false,
}) => {
  const { queryParams, setHistory } = useQueryParams();
  const { showMessage } = useMessage();

  const [searchTerm, _setSearchTerm] = useState(enableHistory && queryParams.search);
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsCount, setSearchResultsCount] = useState({});
  const [invalidSearchTerm, setInvalidSearchTerm] = useState(false);
  const [page, setPage] = useState(1);

  const [defaultSearchResults, setDefaultSearchResults] = useState();
  const [defaultSearchResultsCount, setDefaultSearchResultsCount] = useState();

  const objectNames = objectName && !Array.isArray(objectName) ? [objectName] : objectName || [];
  const objectNamesList = objectName ? objectNames.join(',') : undefined;

  useEffect(() => {
    if (searchTerm) {
      fetchSearchResults(searchTerm);
    }
  }, []);

  useEffect(() => {
    //if (searchTerm !== queryParams.search) {
    //enableHistory && setHistory({search: searchTerm})
    pagination && setPage(1);
    setInvalidSearchTerm(false);
    //}

    if (!searchTerm && defaultSearchResults) {
      setSearchResults(defaultSearchResults);
      setSearchResultsCount(defaultSearchResultsCount);
    }
  }, [searchTerm]);

  const fetchDefaultSearchResults = async (append = false, n = undefined) => {
    try {
      if (!defaultSearchResults || append) {
        n && setPage(prev => n);

        await searchService.getAllByObject(objectNamesList, n, append).then(res => {
          if (res) {
            let results = res.search_results;
            let results_count = res.search_results_count && parseInt(res.search_results_count);

            if (append) {
              results = [...searchResults, ...res.search_results];
            }

            // Save default values for later use
            setDefaultSearchResults(results);
            results_count && setDefaultSearchResultsCount(results_count);

            // Set search results values
            setSearchResults(results);
            results_count && setSearchResultsCount(results_count);
          }
        });
      }
    } catch (err) {
      showMessage(err, 'error');
    } finally {
      setLoading && setLoading(prev => false);
    }
  };

  const fetchSearchResults = async (
    search = undefined,
    append = false,
    n = undefined,
    usePagination = undefined
  ) => {
    try {
      const res = await fetchSearchResultsData(search, append, n, usePagination);

      n && setPage(prev => n);

      if (append) {
        setSearchResults(prev => {
          return [...prev, ...res.searchResults];
        });
      } else {
        setSearchResults(prev => res.searchResults);
      }

      setSearchResultsCount(prev => res.searchResultsCount);

      return res;
    } catch (err) {
      showMessage(err, 'error');
    }
  };

  const fetchSearchResultsData = (
    search = undefined,
    append = false,
    n = undefined,
    usePagination = undefined
  ) => {
    //, append = false, nForced=undefined) => {
    return new Promise(async (resolve, reject) => {
      setLoading && setLoading(true);

      let tmpSearch = searchTerm;
      if (search !== undefined) {
        tmpSearch = search;
      }

      const string = encodeURIComponent(tmpSearch);

      const object = objectNames.includes('all') ? undefined : objectNamesList;

      const pageNumber =
        pagination && usePagination !== false ? (n !== undefined ? n : page) : undefined;

      await searchService
        .getAll(string, object, pageNumber, append)
        .then(res => {
          if (res) {
            if (res.ok) {
              let tmp = {};
              let allCount = 0;
              res.search_results_count.forEach(result => {
                tmp = { ...tmp, [result.object_name]: parseInt(result.count) };
                allCount = allCount + parseInt(result.count);
              });

              tmp = { ...tmp, all: allCount };

              resolve({ searchResults: res.search_results, searchResultsCount: tmp });
            } else {
              setInvalidSearchTerm(true);
              resolve({ searchResults, searchResultsCount });
            }

            setLoading && setLoading(prev => false);
          }
        })
        .catch(err => {
          setLoading && setLoading(prev => false);
          reject(err);
        });
    });
  };

  const clearSearchResults = () => {
    _setSearchTerm('');
    enableHistory && setHistory({ search: '' });
    setSearchResults(defaultSearchResults || []);
    setSearchResultsCount(defaultSearchResultsCount || {});
    setPage(1);
  };

  const setSearchTerm = value => {
    if (value.length > 1) {
      // Only trigger search query for search terms with more than 1 character
      fetchSearchResults(value, false, 1);
    } else if (searchTerm.length > 1 && value.length <= 1) {
      // Clear search results if search term is shortened to less than 2 characters
      clearSearchResults();
    }

    _setSearchTerm(value);
    enableHistory && setHistory({ search: value });
  };

  const fetchAllSearchResults = async search => {
    try {
      const res = await fetchSearchResults(search || searchTerm, undefined, undefined, false);
      return res;
    } catch (err) {
      // do nothing, errors handles in fetchSearchResults
    }
  };

  const loadNextPage = () => {
    const tmpPage = page + 1;

    if (useDefaultValues && !searchTerm) {
      fetchDefaultSearchResults(true, tmpPage);
    } else {
      fetchSearchResults(undefined, true, tmpPage);
    }
  };

  return {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchSearchResults,
    fetchDefaultSearchResults,
    fetchAllSearchResults,
    fetchSearchResultsData,
    clearSearchResults,
    invalidSearchTerm,
    loadNextPage,
  };
};
